/**
 * When a Hub account still owes onboarding, and which step it owes.
 *
 * Mirrors mobile's `resolveUserRoute` (mobile/hooks/use-router.ts) on
 * purpose: "onboarding complete" has to mean the same thing on both
 * platforms, or an account finished on one would be sent back through it on
 * the other. The skippable steps (player profile, Pro offer) leave nothing
 * on the account to check, so, as on mobile, they are not part of this.
 */

const AGE_LIMITS: Record<string, number> = { US: 13, EU: 16, AU: 18, UK: 18 };
const DEFAULT_AGE_LIMIT = 16;

// Locales and `actualCountry` carry ISO 3166 codes, which call the UK "GB"
// and have no "EU" -- so map both onto the keys above, or neither limit applies.
const EU_MEMBER_STATES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE",
  "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
]);

const ageLimitRegion = (country: string): string => {
  const code = country.toUpperCase();
  if (code === "GB") return "UK";
  if (EU_MEMBER_STATES.has(code)) return "EU";
  return code;
};

export const getAgeLimit = (country: string | undefined): number =>
  (country && AGE_LIMITS[ageLimitRegion(country)]) || DEFAULT_AGE_LIMIT;

export const isOldEnough = (
  dobEpochSeconds: number,
  ageLimit: number,
  now: Date = new Date(),
): boolean => {
  const birth = new Date(dobEpochSeconds * 1000);
  let age = now.getUTCFullYear() - birth.getUTCFullYear();
  const monthDiff = now.getUTCMonth() - birth.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < birth.getUTCDate())) {
    age -= 1;
  }
  return age >= ageLimit;
};

/** "en-US" -> "US". Mobile reads the device region; the browser locale is the web's nearest equivalent. */
export const countryFromLocale = (
  locale: string | undefined,
  fallback = "",
): string => {
  const region = locale?.split(/[-_]/)[1];
  return region && /^[A-Za-z]{2}$/.test(region) ? region.toUpperCase() : fallback;
};

export const onboardingCountry = (user?: { actualCountry?: string }): string =>
  countryFromLocale(
    typeof navigator !== "undefined" ? navigator.language : undefined,
    user?.actualCountry ?? "",
  );

/** Noon UTC, so the stored day reads back as the same day in every timezone. */
export const dobToEpochSeconds = (
  year: number,
  monthIndex0: number,
  day: number,
): number => Math.floor(Date.UTC(year, monthIndex0, day, 12) / 1000);

export type OnboardingStep =
  | "dob"
  | "parentRequest"
  | "parentPending"
  | "parentDenied"
  | "profile";

export type OnboardingUser = {
  userType?: string;
  dob?: number;
  username?: string;
  firstName?: string;
  parentPermissionStatus?: string | null;
  parentEmail?: string | null;
};

// Staff-provisioned or staff accounts. Partners are ordinary users and onboard.
const EXEMPT_USER_TYPES = new Set(["admin", "organization", "api"]);

export const nextRequiredOnboardingStep = (
  user: OnboardingUser | null,
  country: string,
  opts: { isImpersonatingFromAdmin?: boolean; now?: Date } = {},
): OnboardingStep | null => {
  if (!user || opts.isImpersonatingFromAdmin) return null;
  if (user.userType && EXEMPT_USER_TYPES.has(user.userType)) return null;

  if (!user.dob) return "dob";
  if (!isOldEnough(user.dob, getAgeLimit(country), opts.now)) {
    // The server writes only parentEmail when the request email goes out;
    // the status stays null until the parent clicks approve or deny. So a
    // stored parentEmail, not a "pending" status, is what means "waiting".
    const status = user.parentPermissionStatus;
    if (status === "denied") return "parentDenied";
    if (status !== "approved") return user.parentEmail ? "parentPending" : "parentRequest";
  }
  if (!user.username || !user.firstName) return "profile";
  return null;
};
